import React, { useEffect, useRef, useState } from "react";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import utahWaterData from "../data/utahWaterData";
import ActionButton1 from "../utils/ActionButton1";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const brand = {
  primary: "#1E5C8A",
  secondary: "#2D7F78",
  aqua: "#74C7C6",
  accent: "#C98A43",
  danger: "#B75A4C",
  deep: "#0E2A3D",
  ink: "#1B3345",
  muted: "#5D6F7F",
  border: "#D7E4EC",
  panel: "#F5FAFC",
  panelAlt: "#ECF4F8",
  panelSoft: "#EEF8F5",
  sand: "#E5C28A",
  foam: "#FCFEFD",
  wetland: "#3F8A63",
};

const sectionThemes = {
  overview: {
    accent: "#1E5C8A",
    border: "#CFDDE8",
    background:
      "radial-gradient(circle at top right, rgba(116, 199, 198, 0.18), transparent 34%), radial-gradient(circle at bottom left, rgba(229, 194, 138, 0.16), transparent 30%), linear-gradient(180deg, rgba(30, 92, 138, 0.10) 0%, rgba(255, 255, 255, 1) 58%)",
    chipBg: "rgba(30, 92, 138, 0.10)",
  },
  statewide: {
    accent: "#1E5C8A",
    border: "#CADAE6",
    background:
      "radial-gradient(circle at top left, rgba(30, 92, 138, 0.16), transparent 34%), radial-gradient(circle at bottom right, rgba(116, 199, 198, 0.14), transparent 28%), linear-gradient(180deg, rgba(14, 42, 61, 0.06) 0%, rgba(255, 255, 255, 1) 58%)",
    chipBg: "rgba(30, 92, 138, 0.10)",
  },
  landscape: {
    accent: "#2D7F78",
    border: "#CEE4DB",
    background:
      "radial-gradient(circle at top right, rgba(45, 127, 120, 0.16), transparent 32%), radial-gradient(circle at bottom left, rgba(116, 199, 198, 0.12), transparent 26%), linear-gradient(180deg, rgba(45, 127, 120, 0.10) 0%, rgba(255, 255, 255, 1) 56%)",
    chipBg: "rgba(45, 127, 120, 0.10)",
  },
  rebates: {
    accent: "#B77936",
    border: "#E8D7C6",
    background:
      "radial-gradient(circle at top left, rgba(229, 194, 138, 0.24), transparent 34%), radial-gradient(circle at bottom right, rgba(201, 138, 67, 0.14), transparent 28%), linear-gradient(180deg, rgba(183, 121, 54, 0.10) 0%, rgba(255, 255, 255, 1) 56%)",
    chipBg: "rgba(183, 121, 54, 0.10)",
  },
  lake: {
    accent: "#2E648D",
    border: "#C9DCE6",
    background:
      "radial-gradient(circle at top right, rgba(116, 199, 198, 0.16), transparent 30%), radial-gradient(circle at bottom left, rgba(46, 100, 141, 0.14), transparent 28%), linear-gradient(180deg, rgba(14, 42, 61, 0.08) 0%, rgba(255, 255, 255, 1) 56%)",
    chipBg: "rgba(46, 100, 141, 0.10)",
  },
  county: {
    accent: "#A96B3A",
    border: "#E6D6C7",
    background:
      "radial-gradient(circle at top left, rgba(201, 138, 67, 0.18), transparent 34%), radial-gradient(circle at bottom right, rgba(229, 194, 138, 0.14), transparent 30%), linear-gradient(180deg, rgba(169, 107, 58, 0.10) 0%, rgba(255, 255, 255, 1) 56%)",
    chipBg: "rgba(169, 107, 58, 0.10)",
  },
};

const pieColors = [brand.primary, brand.accent];
const countyBarPalette = {
  danger: brand.danger,
  caution: brand.accent,
  good: brand.wetland,
  goal: brand.aqua,
};

const chartCardStyle = (themeKey) => ({
  borderColor: sectionThemes[themeKey].border,
  background: `linear-gradient(180deg, ${sectionThemes[themeKey].chipBg} 0%, rgba(255,255,255,1) 62%)`,
  boxShadow: `0 18px 34px ${sectionThemes[themeKey].chipBg}`,
});

const REPORT_SCROLL_OFFSET = 112;

const tableHeadStyle = {
  background:
    "linear-gradient(90deg, rgba(30, 92, 138, 0.10) 0%, rgba(116, 199, 198, 0.12) 100%)",
};

const stickyTableHeadCellStyle = {
  background: "#F1F7FA",
  boxShadow: "inset 0 -1px 0 #D7E4EC",
};

const sharedTooltipStyle = {
  background: "rgba(255,255,255,0.98)",
  border: "1px solid #D7E4EC",
  borderRadius: "18px",
  boxShadow: "0 20px 44px rgba(14, 42, 61, 0.16)",
};

const ChartTooltip = ({ active, payload, label, formatterLabel }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="min-w-[180px] rounded-2xl p-4" style={sharedTooltipStyle}>
      {label ? (
        <p className="mb-3 text-sm font-bold" style={{ color: brand.ink }}>
          {formatterLabel ? formatterLabel(label) : label}
        </p>
      ) : null}
      <div className="space-y-2">
        {payload.map((item) => (
          <div
            key={`${item.name}-${item.dataKey}`}
            className="flex items-center justify-between gap-4 text-sm"
          >
            <span className="flex items-center gap-2" style={{ color: brand.muted }}>
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: item.color }}
              />
              {item.name}
            </span>
            <span className="font-semibold" style={{ color: brand.ink }}>
              {item.name === "GPCD" ||
              item.name === "2024 use" ||
              item.name === "2030 goal"
                ? `${item.value} GPCD`
                : item.name?.includes("diversion")
                  ? `${item.value}%`
                  : item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const formatNumber = (value) =>
  typeof value === "number" ? value.toLocaleString("en-US") : value;

const getMeterWidth = (value, max, min = 10) => {
  if (!max || value <= 0) return "0%";
  const raw = Math.round((value / max) * 100);
  return `${Math.max(min, Math.min(100, raw))}%`;
};

const formatGap = (value) => {
  if (value === 0) return "On target";
  if (value < 0) return `${Math.abs(value)} below goal`;
  return `${value} above goal`;
};

const getGapChipStyle = (gap) => {
  if (gap <= 0) {
    return {
      color: "#047857",
      background: "#d1fae5",
    };
  }

  if (gap <= 35) {
    return {
      color: "#9a3412",
      background: "#ffedd5",
    };
  }

  return {
    color: "#991b1b",
    background: "#fee2e2",
  };
};

const getCountyCurrentBarColor = (county) => {
  if (county.gap <= 0) return countyBarPalette.good;
  if (county.gap <= 35) return countyBarPalette.caution;
  return countyBarPalette.danger;
};

const countyRows = utahWaterData.countyUsage.counties.map((county) => {
  const gap = county.current2024 - county.goal2030;

  return {
    ...county,
    gap,
  };
});

const countiesAtGoal = countyRows.filter((county) => county.gap <= 0).length;
const countiesAboveGoal = countyRows.length - countiesAtGoal;
const furthestAboveGoal = countyRows.reduce((highest, county) =>
  county.gap > highest.gap ? county : highest
);
const alphabeticalCountyRows = [...countyRows].sort((a, b) =>
  a.county.localeCompare(b.county)
);
const closestToGoalAbove = countyRows
  .filter((county) => county.gap > 0)
  .reduce((closest, county) => (county.gap < closest.gap ? county : closest));
const statewideCurrent = utahWaterData.statewideUsageTrends.find(
  (item) => item.year === "2023"
);
const statewideGoal = utahWaterData.statewideUsageTrends.find(
  (item) => item.year === "2030 Goal"
);
const statewideGap = statewideCurrent.gpcd - statewideGoal.gpcd;
const maxCountyGap = Math.max(...countyRows.map((county) => Math.abs(county.gap)));
const maxCountyUse = Math.max(...countyRows.map((county) => county.current2024));
const maxTributaryShare = Math.max(
  ...utahWaterData.greatSaltLake.tributaries.map((item) => item.inflowPercent)
);
const maxTributaryBuffer = Math.max(
  ...utahWaterData.greatSaltLake.tributaries.map(
    (item) => item.conservationBufferAf || 0
  )
);
const trackedConservationBuffer = utahWaterData.greatSaltLake.tributaries.reduce(
  (sum, item) => sum + (item.conservationBufferAf || 0),
  0
);

const landscapeSavingsLevels = {
  "0% (baseline)": 0,
  "30%": 30,
  "50% - 70%": 60,
};

const unitGuide = [
  {
    term: "GPCD",
    meaning: "Gallons per capita per day",
    explanation:
      "This measures how many gallons each person uses on average in one day.",
  },
  {
    term: "AF",
    meaning: "Acre-feet",
    explanation:
      "A western water-volume unit used for reservoirs, rivers, and conservation buffers.",
  },
  {
    term: "cfs",
    meaning: "Cubic feet per second",
    explanation:
      "A flow-rate measure showing how fast water is moving through a river channel.",
  },
];

const utahWaterWaysHighlights = [
  {
    title: "Legislative Foundation",
    detail:
      "Utah Water Ways was formally established through House Bill 307 during the 2023 Utah General Session to address the state's urgent water challenges.",
  },
  {
    title: "Public-Private Partnership",
    detail:
      "It operates as a 501(c)(3) nonprofit that functions as a partnership between government entities and the private sector.",
  },
  {
    title: "Core Mission",
    detail:
      "Its primary goal is to advance the efficient use of water in Utah by building collaborative approaches and encouraging effective state and local policies.",
  },
  {
    title: "Leadership",
    detail:
      "The organization is led by Executive Director Tage Flint, who brings extensive experience in water management to the role.",
  },
  {
    title: "Board of Directors",
    detail:
      "The board is chaired by Greg Miller and includes diverse representation from agriculture, industry, and municipal sectors so multiple water interests are considered.",
  },
  {
    title: "Significant Philanthropy",
    detail:
      "It received a major $2.2 million grant from the Larry H. & Gail Miller Family Foundation to support public messaging and educational outreach.",
  },
  {
    title: "Educational Integration",
    detail:
      "In partnership with the Utah State Board of Education, the organization develops K-12 curriculum materials focused on water conservation and stewardship.",
  },
  {
    title: "Great Salt Lake Focus",
    detail:
      "A major part of its educational and advocacy work highlights the relationship between water use and the health of the Great Salt Lake ecosystem.",
  },
  {
    title: "Water Values Study",
    detail:
      "The organization conducted a comprehensive study to understand the priorities, values, and trade-offs Utahns are willing to make regarding water use.",
  },
  {
    title: "Sustainable Landscaping",
    detail:
      "It heavily promotes water-wise landscaping, often referred to as Localscapes, to reduce the high volume of water used for residential outdoor irrigation.",
  },
  {
    title: "Policy Facilitation",
    detail:
      "Utah Water Ways sponsors and leads high-level policy discussions among state leaders and industry experts to coordinate long-term water supply strategies.",
  },
  {
    title: "Central Information Hub",
    detail:
      "It acts as a repository for water-related data and resources, helping to build cohesiveness among various local and state partners.",
  },
  {
    title: "Public Awareness Campaigns",
    detail:
      "The organization runs statewide messaging campaigns designed to build a strong conservation ethic among Utah residents.",
  },
  {
    title: "Industry Training",
    detail:
      "It provides professional training and development for people working in landscaping, irrigation, and water management sectors.",
  },
  {
    title: "Grant Coordination",
    detail:
      "It helps coordinate and promote various water conservation grants, rebate programs, and sponsorships across the state.",
  },
  {
    title: "Interdisciplinary Approach",
    detail:
      "Its team includes experts in city planning, landscape architecture, and strategic communications to tackle water issues from multiple angles.",
  },
  {
    title: "Economic Consideration",
    detail:
      "The organization works to balance water conservation needs with the state's economic growth and agricultural preservation.",
  },
  {
    title: "Statewide Scope",
    detail:
      "Its initiatives are designed to impact all regions of Utah, from the urban Wasatch Front to rural agricultural communities.",
  },
  {
    title: "Legislative Accountability",
    detail:
      "It is required to report annually to the Natural Resources, Agriculture, and Environment Interim Committee to ensure transparency and progress.",
  },
  {
    title: "Future-Focused Vision",
    detail:
      "Its long-term vision is a water-efficient future that supports sustainable resources, economic strength, and environmental well-being for future generations of Utahns.",
  },
];

const reportNavigation = [
  {
    key: "statewide-water",
    label: "Per-capita use",
    description: "How much water is being used per capita",
    styleKey: "Statewide",
  },
  {
    key: "landscaping-impact",
    label: "Water-wise savings",
    description: "When people switch from lawn to water-wise usage, how much water is saved",
    styleKey: "Landscaping",
  },
  {
    key: "water-rebates",
    label: "Utah rebates",
    description: "In Utah some people got rebates",
    styleKey: "Rebates",
  },
  {
    key: "great-salt-lake",
    label: "Lake impact",
    description: "If water is saved from residential usage, how can it positively affect Great Salt Lake",
    styleKey: "Lake Impact",
  },
  {
    key: "county-water",
    label: "Usage measure",
    description: "Counties data to get a measure of usage",
    styleKey: "Counties",
  },
];

const summaryCards = [
  {
    eyebrow: "Statewide Use",
    label: "Utah used 207 GPCD in 2023",
    value: "207",
    unit: "gallons per person per day",
    note: "This is the latest statewide usage value in the report.",
    accent: brand.primary,
    glow: "rgba(30, 92, 138, 0.16)",
    icon: "statewide",
    progress: 84,
  },
  {
    eyebrow: "2030 Target",
    label: "The statewide goal is 202 GPCD",
    value: "5",
    unit: "gallons away",
    note: "Utah is 5 gallons per person per day above the target benchmark.",
    accent: brand.accent,
    glow: "rgba(201, 138, 67, 0.16)",
    icon: "target",
    progress: 30,
  },
  {
    eyebrow: "Landscape Conversions",
    label: "Turf removed in 2024",
    value: "3.1M sq. ft.",
    unit: "converted",
    note: "The turf buyout program replaced lawn area with water-wise landscaping.",
    accent: brand.secondary,
    glow: "rgba(45, 127, 120, 0.16)",
    icon: "landscape",
    progress: 78,
  },
  {
    eyebrow: "County Progress",
    label: "Counties at or below their 2030 goal",
    value: `${countiesAtGoal}/${countyRows.length}`,
    unit: `${countiesAboveGoal} still above goal`,
    note: "County performance varies a lot, which makes local action important.",
    accent: sectionThemes.county.accent,
    glow: "rgba(169, 107, 58, 0.16)",
    icon: "county",
    progress: 48,
  },
];

const statewideBarColors = {
  2015: "#A8BFD1",
  2019: brand.aqua,
  2020: "#D5AA72",
  2023: brand.primary,
  "2030 Goal": brand.wetland,
};

const countyViewButtons = [
  {
    key: "largestGap",
    label: "Largest gaps",
    helper: "Counties furthest above their goal",
  },
  {
    key: "highestUse",
    label: "Highest use",
    helper: "Counties with the highest 2024 GPCD",
  },
  {
    key: "closestToGoal",
    label: "Closest above goal",
    helper: "Counties that are near the target line",
  },
  {
    key: "atGoal",
    label: "At or below goal",
    helper: "Counties already meeting the goal",
  },
];

const getCountyChartData = (view) => {
  if (view === "highestUse") {
    return [...countyRows]
      .sort((a, b) => b.current2024 - a.current2024)
      .slice(0, 8);
  }

  if (view === "closestToGoal") {
    return [...countyRows]
      .filter((county) => county.gap > 0)
      .sort((a, b) => a.gap - b.gap)
      .slice(0, 8);
  }

  if (view === "atGoal") {
    return [...countyRows]
      .filter((county) => county.gap <= 0)
      .sort((a, b) => a.gap - b.gap)
      .slice(0, 8);
  }

  return [...countyRows].sort((a, b) => b.gap - a.gap).slice(0, 8);
};

const MiniMeter = ({
  value,
  max,
  color,
  track = "#e5eef7",
  className = "mt-3",
}) => (
  <div className={className}>
    <div className="h-2 rounded-full" style={{ background: track }}>
      <div
        className="h-2 rounded-full"
        style={{
          width: getMeterWidth(value, max),
          background: color,
          boxShadow: `0 8px 18px ${color}33`,
        }}
      />
    </div>
  </div>
);

const SnapshotCard = ({
  label,
  value,
  note,
  accent,
  glow,
  compact = false,
}) => (
  <div
    className={`rounded-3xl border ${compact ? "p-4" : "p-5"}`}
    style={{
      borderColor: `${accent}26`,
      background: `linear-gradient(180deg, ${glow} 0%, rgba(255,255,255,1) 72%)`,
      boxShadow: `0 18px 30px ${glow}`,
    }}
  >
    <p
      className="text-[10px] font-semibold uppercase tracking-[0.16em] sm:text-[11px] sm:tracking-[0.24em]"
      style={{ color: accent }}
    >
      {label}
    </p>
    <p
      className={`mt-3 break-words font-bold leading-tight ${
        compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"
      }`}
      style={{ color: brand.ink }}
    >
      {value}
    </p>
    {note ? (
      <p className="mt-2 text-sm leading-6" style={{ color: brand.muted }}>
        {note}
      </p>
    ) : null}
  </div>
);

const KpiIcon = ({ type, color }) => {
  const common = {
    fill: "none",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  if (type === "target") {
    return (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <circle cx="12" cy="12" r="7" {...common} />
        <circle cx="12" cy="12" r="3" {...common} />
        <path d="M12 3v2.5M21 12h-2.5M12 21v-2.5M3 12h2.5" {...common} />
      </svg>
    );
  }

  if (type === "landscape") {
    return (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path d="M12 20V11" {...common} />
        <path d="M12 11c0-3.5 2.2-6 6-7-1 4.6-3.7 7-6 7Z" {...common} />
        <path d="M12 14c0-3-1.8-5.2-5-6.4.4 4.4 2.5 6.4 5 6.4Z" {...common} />
      </svg>
    );
  }

  if (type === "county") {
    return (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path d="M5 19V9M12 19V5M19 19v-7" {...common} />
        <path d="M3 19h18" {...common} />
        <circle cx="5" cy="9" r="1.2" fill={color} stroke="none" />
        <circle cx="12" cy="5" r="1.2" fill={color} stroke="none" />
        <circle cx="19" cy="12" r="1.2" fill={color} stroke="none" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <path d="M12 3.5c4.8 0 8.5 3.8 8.5 8.5S16.8 20.5 12 20.5 3.5 16.8 3.5 12C3.5 8.3 5.7 5 9 4" {...common} />
      <path d="M11 7.5c2.5 0 4.5 2 4.5 4.5" {...common} />
      <path d="M8.2 14.4c.8 1 2.1 1.6 3.4 1.6 2.3 0 4.2-1.9 4.2-4.2" {...common} />
      <path d="M8 6.5c0 1.4-1.1 2.5-2.5 2.5S3 7.9 3 6.5 4.1 4 5.5 4 8 5.1 8 6.5Z" {...common} />
    </svg>
  );
};

const OverviewKpiCard = ({
  eyebrow,
  label,
  value,
  unit,
  accent,
  glow,
  icon,
  progress,
}) => (
  <div
    className="flex h-full flex-col rounded-3xl border p-4 sm:p-5"
    style={{
      borderColor: `${accent}26`,
      background: `linear-gradient(180deg, ${glow} 0%, rgba(255,255,255,0.98) 72%)`,
      boxShadow: `0 18px 30px ${glow}`,
    }}
  >
    <div className="flex items-start justify-between gap-4">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
        style={{
          background: `${accent}14`,
          boxShadow: `inset 0 0 0 1px ${accent}22, 0 10px 22px ${glow}`,
        }}
      >
        <KpiIcon type={icon} color={accent} />
      </div>
      <p
        className="text-right text-[10px] font-semibold uppercase tracking-[0.16em] sm:text-xs sm:tracking-widest"
        style={{ color: accent }}
      >
        {eyebrow}
      </p>
    </div>
    <p
      className="mt-4 break-words text-xl font-bold leading-tight sm:text-2xl"
      style={{ color: brand.ink }}
    >
      {value}
    </p>
    <p className="mt-1 break-words text-sm font-semibold leading-5" style={{ color: accent }}>
      {unit}
    </p>
    <h3
      className="mt-2 break-words text-sm font-bold leading-5"
      style={{ color: brand.ink }}
    >
      {label}
    </h3>
    <div className="mt-auto pt-3">
      <MiniMeter
        value={progress}
        max={100}
        color={accent}
        track="#e5eef7"
        className=""
      />
    </div>
  </div>
);

const SectionHeader = ({ eyebrow, title, subtitle, accent = brand.primary }) => (
  <div className="mb-4 sm:mb-5">
    <p
      className="text-sm font-semibold uppercase tracking-widest"
      style={{ color: accent }}
    >
      {eyebrow}
    </p>
    <h2 className="mt-2 text-2xl font-bold sm:text-3xl" style={{ color: brand.ink }}>
      {title}
    </h2>
    {subtitle ? (
      <p className="mt-2 max-w-3xl text-sm leading-6" style={{ color: brand.muted }}>
        {subtitle}
      </p>
    ) : null}
  </div>
);

const FloatingReportNav = ({
  items,
  activeKey,
  visible,
  onSelect,
}) => {
  if (!visible) return null;

  return (
    <aside
      className="fixed left-4 top-[168px] z-30 hidden max-h-[calc(100vh-188px)] w-[220px] overflow-y-auto pb-3 xl:block"
      aria-label="Quick report navigation"
    >
      <div
        className="rounded-[28px] border p-3"
        style={{
          borderColor: brand.border,
          background: "rgba(252, 254, 253, 0.92)",
          backdropFilter: "blur(14px)",
          boxShadow: "0 22px 44px rgba(14, 42, 61, 0.16)",
        }}
      >
        <p
          className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: brand.primary }}
        >
          Quick Report Nav
        </p>
        <div className="mt-3 space-y-2">
          {items.map((item, index) => {
            const isActive = activeKey === item.key;

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onSelect(item.key)}
                className="flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-left transition-transform duration-200 hover:-translate-y-0.5"
                style={{
                  border: `1px solid ${isActive ? `${brand.primary}22` : "#E5EDF2"}`,
                  background: isActive
                    ? "linear-gradient(180deg, rgba(30, 92, 138, 0.10) 0%, rgba(255,255,255,1) 100%)"
                    : "rgba(255,255,255,0.88)",
                  boxShadow: isActive
                    ? "0 10px 22px rgba(30, 92, 138, 0.14)"
                    : "0 6px 14px rgba(27, 51, 69, 0.05)",
                }}
                aria-pressed={isActive}
              >
                <span
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                  style={{
                    background: isActive ? brand.primary : "rgba(30, 92, 138, 0.10)",
                    color: isActive ? "#ffffff" : brand.primary,
                  }}
                >
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p
                    className="text-sm font-semibold leading-5"
                    style={{ color: isActive ? brand.primary : brand.ink }}
                  >
                    {item.label}
                  </p>
                  <p className="mt-1 text-xs leading-5" style={{ color: brand.muted }}>
                    {item.styleKey}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

const Panel = ({ id, children, theme = "overview" }) => (
  <section
    id={id}
    className="mb-8 rounded-3xl border bg-white p-5 shadow-sm sm:p-6 lg:p-7"
    style={{
      scrollMarginTop: `${REPORT_SCROLL_OFFSET}px`,
      borderColor: sectionThemes[theme].border,
      background: sectionThemes[theme].background,
      boxShadow: `0 18px 40px ${sectionThemes[theme].chipBg}`,
    }}
  >
    {children}
  </section>
);

const LegacyWaterWaysModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close Utah Water Ways modal"
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: "rgba(14, 42, 61, 0.56)", backdropFilter: "blur(6px)" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="utah-water-ways-title"
        className="relative z-10 max-h-[88vh] w-full max-w-5xl overflow-hidden rounded-[32px] border bg-white"
        style={{
          borderColor: brand.border,
          background:
            "radial-gradient(circle at top right, rgba(116, 199, 198, 0.18), transparent 28%), radial-gradient(circle at top left, rgba(229, 194, 138, 0.16), transparent 26%), linear-gradient(180deg, rgba(245, 250, 252, 1) 0%, rgba(255,255,255,1) 100%)",
          boxShadow: "0 30px 80px rgba(14, 42, 61, 0.26)",
        }}
      >
        <div
          className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6"
          style={{ borderColor: brand.border }}
        >
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.22em]"
              style={{ color: brand.primary }}
            >
              Utah Water Ways
            </p>
            <h2
              id="utah-water-ways-title"
              className="mt-2 text-2xl font-bold sm:text-3xl"
              style={{ color: brand.ink }}
            >
              20 key points about the organization
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6" style={{ color: brand.muted }}>
              This reference opens on demand so the main report stays focused while still
              giving readers context about one of Utah&apos;s central water organizations.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border"
            style={{
              borderColor: brand.border,
              background: brand.foam,
              color: brand.primary,
              boxShadow: "0 10px 22px rgba(14, 42, 61, 0.08)",
            }}
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="max-h-[calc(88vh-90px)] overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
          <div
            className="mb-5 rounded-3xl border p-4 sm:p-5"
            style={{
              borderColor: sectionThemes.overview.border,
              background:
                "linear-gradient(180deg, rgba(30, 92, 138, 0.08) 0%, rgba(255,255,255,1) 100%)",
            }}
          >
            <p className="text-sm leading-7" style={{ color: brand.muted }}>
              Source website:
              {" "}
              <a
                href="https://utahwaterways.org/"
                target="_blank"
                rel="noreferrer"
                className="font-semibold hover:underline"
                style={{ color: brand.primary }}
              >
                utahwaterways.org
              </a>
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {utahWaterWaysHighlights.map((item, index) => (
              <div
                key={item.title}
                className="rounded-3xl border p-4 sm:p-5"
                style={{
                  borderColor: brand.border,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(236, 244, 248, 0.92) 100%)",
                  boxShadow: "0 14px 26px rgba(14, 42, 61, 0.06)",
                }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                    style={{
                      background: "rgba(30, 92, 138, 0.12)",
                      color: brand.primary,
                    }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="text-base font-bold" style={{ color: brand.ink }}>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: brand.muted }}>
                      {item.detail}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const WaterWaysModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      style={{ background: "rgba(14, 42, 61, 0.56)", backdropFilter: "blur(8px)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="utah-water-ways-title"
        aria-describedby="utah-water-ways-description"
        tabIndex={-1}
        className="relative z-10 flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border bg-white"
        style={{
          borderColor: brand.border,
          background:
            "radial-gradient(circle at top right, rgba(116, 199, 198, 0.18), transparent 28%), radial-gradient(circle at top left, rgba(229, 194, 138, 0.16), transparent 26%), linear-gradient(180deg, rgba(245, 250, 252, 1) 0%, rgba(255,255,255,1) 100%)",
          boxShadow: "0 30px 80px rgba(14, 42, 61, 0.26)",
        }}
      >
        <div
          className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6"
          style={{ borderColor: brand.border }}
        >
          <div className="min-w-0">
            <p
              className="text-xs font-semibold uppercase tracking-[0.22em]"
              style={{ color: brand.primary }}
            >
              utahwaterways.org
            </p>
            <h2
              id="utah-water-ways-title"
              className="mt-2 text-2xl font-bold sm:text-3xl"
              style={{ color: brand.ink }}
            >
              Utah Water Ways overview
            </h2>
            <p
              id="utah-water-ways-description"
              className="mt-2 max-w-3xl text-sm leading-6"
              style={{ color: brand.muted }}
            >
              Utah Water Ways is a central Utah organization focused on conservation,
              public education, policy coordination, and long-term water stewardship.
              This modal keeps that reference available without interrupting the main report.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href="https://utahwaterways.org/"
              target="_blank"
              rel="noreferrer"
              className="hidden rounded-2xl border px-4 py-2 text-sm font-semibold sm:inline-flex"
              style={{
                borderColor: brand.border,
                background: brand.foam,
                color: brand.primary,
                boxShadow: "0 10px 22px rgba(14, 42, 61, 0.08)",
              }}
            >
              Visit Site
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close Utah Water Ways modal"
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border"
              style={{
                borderColor: brand.border,
                background: brand.foam,
                color: brand.primary,
                boxShadow: "0 10px 22px rgba(14, 42, 61, 0.08)",
              }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6 6 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div
              className="rounded-3xl border p-4 sm:p-5"
              style={{
                borderColor: sectionThemes.overview.border,
                background:
                  "linear-gradient(180deg, rgba(30, 92, 138, 0.08) 0%, rgba(255,255,255,1) 100%)",
              }}
            >
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: brand.primary }}
              >
                About UtahWaterWays.org
              </p>
              <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                <a
                  href="https://utahwaterways.org/"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold hover:underline"
                  style={{ color: brand.primary }}
                >
                  UtahWaterWays.org
                </a>
                {" "}
                serves as a public-facing reference for Utah&apos;s collaborative water
                education and conservation efforts, connecting stewardship, policy,
                landscaping, and statewide awareness work.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <div
                className="rounded-3xl border p-4"
                style={{
                  borderColor: brand.border,
                  background: "linear-gradient(180deg, rgba(45, 127, 120, 0.10) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: brand.secondary }}>
                  Type
                </p>
                <p className="mt-2 text-sm font-bold" style={{ color: brand.ink }}>
                  501(c)(3) nonprofit partnership
                </p>
              </div>
              <div
                className="rounded-3xl border p-4"
                style={{
                  borderColor: brand.border,
                  background: "linear-gradient(180deg, rgba(201, 138, 67, 0.10) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: brand.accent }}>
                  Established
                </p>
                <p className="mt-2 text-sm font-bold" style={{ color: brand.ink }}>
                  Created in 2023 through HB 307
                </p>
              </div>
              <div
                className="rounded-3xl border p-4"
                style={{
                  borderColor: brand.border,
                  background: "linear-gradient(180deg, rgba(63, 138, 99, 0.10) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: brand.wetland }}>
                  Focus
                </p>
                <p className="mt-2 text-sm font-bold" style={{ color: brand.ink }}>
                  Conservation, education, policy, stewardship
                </p>
              </div>
            </div>
          </div>

          <div
            className="mt-5 rounded-3xl border p-4 sm:p-5"
            style={{
              borderColor: brand.border,
              background:
                "linear-gradient(180deg, rgba(236, 244, 248, 0.78) 0%, rgba(255,255,255,1) 100%)",
            }}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p
                  className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                  style={{ color: brand.primary }}
                >
                  Reference List
                </p>
                <p className="mt-1 text-sm leading-6" style={{ color: brand.muted }}>
                  The 20 points below explain Utah Water Ways and why the organization
                  matters in Utah&apos;s water conservation and policy landscape.
                </p>
              </div>
              <a
                href="https://utahwaterways.org/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex rounded-2xl border px-4 py-2 text-sm font-semibold hover:underline"
                style={{
                  borderColor: brand.border,
                  background: brand.foam,
                  color: brand.primary,
                }}
              >
                Open UtahWaterWays.org
              </a>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {utahWaterWaysHighlights.map((item, index) => (
              <div
                key={item.title}
                className="rounded-3xl border p-4 sm:p-5"
                style={{
                  borderColor: brand.border,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(236, 244, 248, 0.92) 100%)",
                  boxShadow: "0 14px 26px rgba(14, 42, 61, 0.06)",
                }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                    style={{
                      background: "rgba(30, 92, 138, 0.12)",
                      color: brand.primary,
                    }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="text-base font-bold" style={{ color: brand.ink }}>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: brand.muted }}>
                      {item.detail}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="flex items-center justify-end gap-3 border-t px-5 py-4 sm:px-6"
          style={{
            borderColor: brand.border,
            background: "rgba(255,255,255,0.92)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="inline-flex rounded-2xl border px-4 py-2.5 text-sm font-semibold"
            style={{
              borderColor: brand.border,
              background: brand.foam,
              color: brand.primary,
            }}
          >
            Close
          </button>
          <a
            href="https://utahwaterways.org/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex rounded-2xl px-4 py-2.5 text-sm font-semibold text-white"
            style={{
              background:
                "linear-gradient(135deg, rgba(30, 92, 138, 0.96) 0%, rgba(45, 127, 120, 0.92) 100%)",
              boxShadow: "0 12px 24px rgba(14, 42, 61, 0.16)",
            }}
          >
            Visit UtahWaterWays.org
          </a>
        </div>
      </div>
    </div>
  );
};

const UtahWater = () => {
  const [countyView, setCountyView] = useState("largestGap");
  const [activeReport, setActiveReport] = useState("statewide-water");
  const [displayedReport, setDisplayedReport] = useState("statewide-water");
  const [isReportVisible, setIsReportVisible] = useState(true);
  const [isWaterWaysModalOpen, setIsWaterWaysModalOpen] = useState(false);
  const [isFloatingReportNavVisible, setIsFloatingReportNavVisible] = useState(false);
  const reportSwitchTimeoutRef = useRef(null);
  const reportNavSectionRef = useRef(null);
  const reportContentRef = useRef(null);
  const reportContentEndRef = useRef(null);
  const countyChartData = getCountyChartData(countyView);
  const countyViewMeta = countyViewButtons.find((item) => item.key === countyView);

  const scrollToReportContent = () => {
    if (!reportContentRef.current) return;

    const top =
      reportContentRef.current.getBoundingClientRect().top +
      window.scrollY -
      REPORT_SCROLL_OFFSET;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: "smooth",
    });
  };

  useEffect(() => {
    return () => {
      if (reportSwitchTimeoutRef.current) {
        clearTimeout(reportSwitchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isWaterWaysModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsWaterWaysModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isWaterWaysModalOpen]);

  useEffect(() => {
    const updateFloatingNavVisibility = () => {
      if (
        isWaterWaysModalOpen ||
        !reportNavSectionRef.current ||
        !reportContentEndRef.current
      ) {
        setIsFloatingReportNavVisible(false);
        return;
      }

      const start =
        reportNavSectionRef.current.getBoundingClientRect().top +
        window.scrollY -
        REPORT_SCROLL_OFFSET -
        32;
      const end =
        reportContentEndRef.current.getBoundingClientRect().top +
        window.scrollY -
        window.innerHeight * 0.4;
      const currentY = window.scrollY;

      setIsFloatingReportNavVisible(currentY >= start && currentY <= end);
    };

    updateFloatingNavVisibility();
    window.addEventListener("scroll", updateFloatingNavVisibility, { passive: true });
    window.addEventListener("resize", updateFloatingNavVisibility);

    return () => {
      window.removeEventListener("scroll", updateFloatingNavVisibility);
      window.removeEventListener("resize", updateFloatingNavVisibility);
    };
  }, [displayedReport, isWaterWaysModalOpen]);

  const handleReportSelect = (reportKey) => {
    if (reportKey === activeReport) return;

    setActiveReport(reportKey);
    setIsReportVisible(false);

    if (reportSwitchTimeoutRef.current) {
      clearTimeout(reportSwitchTimeoutRef.current);
    }

    reportSwitchTimeoutRef.current = setTimeout(() => {
      setDisplayedReport(reportKey);
      requestAnimationFrame(() => {
        setIsReportVisible(true);
        requestAnimationFrame(() => {
          scrollToReportContent();
        });
      });
    }, 180);
  };

  return (
    <div
      className="min-h-screen w-full text-black"
      style={{
        background:
          "radial-gradient(circle at top left, rgba(30, 92, 138, 0.14), transparent 24%), radial-gradient(circle at top right, rgba(116, 199, 198, 0.12), transparent 24%), radial-gradient(circle at bottom right, rgba(229, 194, 138, 0.14), transparent 26%), linear-gradient(180deg, #f5fafc 0%, #ffffff 100%)",
      }}
    >
      <div
        aria-hidden={isWaterWaysModalOpen}
        className={isWaterWaysModalOpen ? "pointer-events-none select-none opacity-0" : "opacity-100"}
        style={{
          transition: "opacity 180ms ease",
        }}
      >
      <FloatingReportNav
        items={reportNavigation}
        activeKey={activeReport}
        visible={isFloatingReportNavVisible}
        onSelect={handleReportSelect}
      />
      <LandingSection
        data={{
          imageName: "utah-water.webp",
          title: "Utah Water",
          visible: "",
          overlay:
            "linear-gradient(rgba(14, 42, 61, 0.66), rgba(30, 92, 138, 0.46))",
        }}
      />
      <main className="max-w-7xl mx-auto px-4 pb-8 pt-6 sm:px-6 sm:pb-10 sm:pt-8 lg:px-8">
        <section className="mb-6">
          <div
            className="rounded-3xl border bg-white px-5 py-6 shadow-sm sm:px-7 sm:py-7"
            style={{
              borderColor: brand.border,
              background:
                "radial-gradient(circle at top right, rgba(116, 199, 198, 0.20), transparent 30%), radial-gradient(circle at bottom left, rgba(229, 194, 138, 0.16), transparent 28%), linear-gradient(180deg, rgba(30, 92, 138, 0.10) 0%, rgba(255,255,255,1) 64%)",
              boxShadow: "0 26px 50px rgba(14, 42, 61, 0.12)",
            }}
          >
            <div className="max-w-4xl">
              <div>
                <p
                  className="text-sm font-semibold uppercase tracking-widest"
                  style={{ color: brand.primary }}
                >
                  Utah Water Report
                </p>
                <h1
                  className="mt-3 text-4xl font-bold leading-tight"
                  style={{ color: brand.ink }}
                >
                  {utahWaterData.thesis}
                </h1>
                <p className="mt-3 max-w-3xl text-base leading-7" style={{ color: brand.muted }}>
                  {utahWaterData.subtitle}
                </p>
              </div>
            </div>
          </div>
        </section>

        <Panel id="water-kpis" theme="overview">
          <SectionHeader
            eyebrow="Quick Snapshot"
            title="Key water indicators at a glance"
            subtitle="These KPIs establish the statewide baseline first, then the report navigation below lets readers move into the detailed sections."
            accent={sectionThemes.overview.accent}
          />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <OverviewKpiCard key={card.label} {...card} />
            ))}
          </div>
        </Panel>

        <section ref={reportNavSectionRef} className="mb-8">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {reportNavigation.map((item) => (
            <div
              key={item.key}
              className="rounded-3xl border p-4"
              style={{
                borderColor: activeReport === item.key ? brand.border : "#E5EDF2",
                background:
                  activeReport === item.key
                    ? "linear-gradient(180deg, rgba(30, 92, 138, 0.08) 0%, rgba(255,255,255,1) 100%)"
                    : "rgba(255,255,255,0.92)",
                boxShadow:
                  activeReport === item.key
                    ? "0 14px 28px rgba(14, 42, 61, 0.10)"
                    : "0 10px 18px rgba(27, 51, 69, 0.05)",
              }}
            >
              <ActionButton1
                data={item.label}
                onClick={() => handleReportSelect(item.key)}
                wrapperClassName="block w-full"
                buttonClassName="w-full justify-between text-left"
                buttonStyle={{
                  width: "100%",
                  background: activeReport === item.key ? brand.primary : brand.foam,
                  color: activeReport === item.key ? "#ffffff" : brand.primary,
                  borderRadius: "18px",
                  transform: activeReport === item.key
                    ? "translateY(-2px) scale(1.02)"
                    : "none",
                  boxShadow: activeReport === item.key
                    ? "0 10px 24px rgba(30, 92, 138, 0.22)"
                    : "0 4px 12px rgba(27, 51, 69, 0.08)",
                  letterSpacing: activeReport === item.key ? "0.03em" : "0.01em",
                }}
                ariaPressed={activeReport === item.key}
              />
              <p
                className="mt-3 text-sm leading-6"
                style={{ color: brand.muted }}
              >
                {item.description}
              </p>
            </div>
          ))}
          </div>
        </section>

        <div
          ref={reportContentRef}
          style={{
            opacity: isReportVisible ? 1 : 0,
            transform: isReportVisible
              ? "translateY(0px) scale(1)"
              : "translateY(18px) scale(0.985)",
            filter: isReportVisible ? "blur(0px)" : "blur(4px)",
            transition:
              "opacity 220ms ease, transform 280ms ease, filter 280ms ease",
            willChange: "opacity, transform, filter",
          }}
        >
        {displayedReport === "statewide-water" ? (
        <Panel id="statewide-water" theme="statewide">
          <SectionHeader
            eyebrow="Statewide Picture"
            title="Usage trends and where the water goes"
            subtitle="These two visuals answer the first two questions most readers ask: how close Utah is to the statewide goal, and which sectors use the most water."
            accent={sectionThemes.statewide.accent}
          />

          <div className="mb-6 grid gap-4 lg:grid-cols-3">
            <SnapshotCard
              label="Latest Statewide Use"
              value={`${statewideCurrent.gpcd} GPCD`}
              note="Utah's latest statewide reading is close to the 2030 benchmark."
              accent={brand.primary}
              glow="rgba(11, 79, 138, 0.14)"
              compact
            />
            <SnapshotCard
              label="2030 Goal"
              value={`${statewideGoal.gpcd} GPCD`}
              note="This is the target line used across the report."
              accent={brand.wetland}
              glow="rgba(63, 138, 99, 0.14)"
              compact
            />
            <SnapshotCard
              label="Gap To Target"
              value={`${statewideGap} GPCD`}
              note="A small statewide gap means local savings can materially change the outcome."
              accent={brand.accent}
              glow="rgba(201, 138, 67, 0.14)"
              compact
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <div
              className="rounded-3xl border p-5"
              style={chartCardStyle("statewide")}
            >
              <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                Statewide water usage trends
              </h3>
              <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                Lower bars are better. The dashed target line marks the 2030 goal so readers
                can immediately see whether statewide usage is above or below goal.
              </p>
              <div className="mt-6 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={utahWaterData.statewideUsageTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#d8e6fb" />
                    <XAxis dataKey="year" tick={{ fontSize: 12, fill: brand.muted }} />
                    <YAxis tick={{ fontSize: 12, fill: brand.muted }} />
                    <Tooltip
                      content={
                        <ChartTooltip
                          formatterLabel={(label) => `${label}`}
                        />
                      }
                    />
                    <Bar dataKey="gpcd" name="GPCD" radius={[10, 10, 0, 0]}>
                      {utahWaterData.statewideUsageTrends.map((entry) => (
                        <Cell
                          key={`statewide-${entry.year}`}
                          fill={statewideBarColors[entry.year] || brand.primary}
                        />
                      ))}
                    </Bar>
                    <ReferenceLine
                      y={202}
                      stroke={brand.deep}
                      strokeWidth={2.5}
                      strokeDasharray="5 5"
                      ifOverflow="extendDomain"
                      label={{
                        value: "2030 goal",
                        fill: brand.deep,
                        position: "insideTopRight",
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <span
                  className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ background: "rgba(30, 92, 138, 0.12)", color: brand.primary }}
                >
                  2023 statewide use: 207 GPCD
                </span>
                <span
                  className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ background: "rgba(201, 138, 67, 0.14)", color: brand.accent }}
                >
                  2030 goal line: 202 GPCD
                </span>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                {utahWaterData.statewideUsageTrends.map((item) => (
                  <div
                    key={item.year}
                    className="flex items-start justify-between rounded-2xl border bg-white px-4 py-3"
                    style={{ borderColor: brand.border }}
                  >
                    <div>
                      <p className="font-semibold" style={{ color: brand.ink }}>
                        {item.year}
                      </p>
                      <p className="text-sm" style={{ color: brand.muted }}>
                        {item.status}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: brand.primary }}>
                        {item.gpcd}
                      </p>
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: brand.muted }}>
                        GPCD
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="rounded-3xl border p-5"
              style={{
                ...chartCardStyle("statewide"),
                background:
                  "linear-gradient(180deg, rgba(116, 199, 198, 0.14) 0%, rgba(255,255,255,1) 58%)",
              }}
            >
              <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                Water use breakdown by sector
              </h3>
              <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                Agriculture dominates total diversion. The residential story matters
                because it sits inside the municipal share, where conservation tools
                like rebates and lawn conversion can move behavior quickly.
              </p>
              <div className="mt-6 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={utahWaterData.sectorBreakdown}
                      dataKey="share"
                      nameKey="sector"
                      innerRadius={75}
                      outerRadius={110}
                      paddingAngle={4}
                    >
                      {utahWaterData.sectorBreakdown.map((entry, index) => (
                        <Cell
                          key={entry.sector}
                          fill={pieColors[index % pieColors.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={<ChartTooltip formatterLabel={(label) => `${label}`} />}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {utahWaterData.sectorBreakdown.map((item, index) => (
                  <span
                    key={item.sector}
                    className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                    style={{
                      background: `${pieColors[index % pieColors.length]}18`,
                      color: pieColors[index % pieColors.length],
                    }}
                  >
                    {item.sector}: {item.share}%
                  </span>
                ))}
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {utahWaterData.sectorBreakdown.map((item) => (
                  <div
                    key={item.sector}
                    className="rounded-2xl border bg-white p-4"
                    style={{ borderColor: brand.border }}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-lg font-bold" style={{ color: brand.ink }}>
                        {item.sector}
                      </h3>
                      <span className="text-2xl font-bold" style={{ color: brand.primary }}>
                        {item.share}%
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6" style={{ color: brand.muted }}>
                      {item.useCase}
                    </p>
                    <MiniMeter
                      value={item.share}
                      max={100}
                      color={item.sector === "Agriculture" ? brand.primary : brand.aqua}
                      track="#e4eff8"
                    />
                  </div>
                ))}
              </div>

              <div
                className="mt-5 rounded-3xl border p-4 sm:p-5"
                style={{
                  borderColor: sectionThemes.statewide.border,
                  background:
                    "linear-gradient(180deg, rgba(30, 92, 138, 0.10) 0%, rgba(255,255,255,1) 52%)",
                }}
              >
                <h3 className="text-lg font-bold" style={{ color: brand.ink }}>
                  Inside Municipal & Industrial use
                </h3>
                <p className="mt-2 text-sm leading-7" style={{ color: brand.muted }}>
                  Most of the municipal share is still residential, and most of that
                  residential use happens outdoors.
                </p>
                <div className="mt-4 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
                  {utahWaterData.municipalResidentialBreakdown.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-3xl border p-4 sm:p-5"
                      style={{
                        borderColor: sectionThemes.statewide.border,
                        background:
                          item.label === "Outdoor Residential"
                            ? "linear-gradient(180deg, rgba(116, 199, 198, 0.14) 0%, rgba(255,255,255,1) 100%)"
                            : "linear-gradient(180deg, rgba(30, 92, 138, 0.08) 0%, rgba(255,255,255,1) 100%)",
                        boxShadow:
                          item.label === "Outdoor Residential"
                            ? "0 14px 28px rgba(116, 199, 198, 0.14)"
                            : "0 14px 28px rgba(30, 92, 138, 0.10)",
                      }}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p
                            className="text-[10px] font-semibold uppercase tracking-[0.16em] sm:text-[11px] sm:tracking-[0.24em]"
                            style={{
                              color:
                                item.label === "Outdoor Residential"
                                  ? brand.secondary
                                  : brand.primary,
                            }}
                          >
                            Residential Use
                          </p>
                          <h3
                            className="mt-2 text-base font-bold leading-6 sm:text-lg"
                            style={{ color: brand.ink }}
                          >
                            {item.label}
                          </h3>
                        </div>
                        <span
                          className="inline-flex w-fit shrink-0 self-start rounded-2xl px-3 py-2 text-xl font-bold sm:px-4 sm:text-2xl"
                          style={{
                            color:
                              item.label === "Outdoor Residential"
                                ? brand.secondary
                                : brand.primary,
                            background:
                              item.label === "Outdoor Residential"
                                ? "rgba(45, 127, 120, 0.10)"
                                : "rgba(30, 92, 138, 0.10)",
                          }}
                        >
                          {item.shareLabel}
                        </span>
                      </div>
                      <MiniMeter
                        value={item.shareValue}
                        max={100}
                        color={
                          item.label === "Outdoor Residential"
                            ? brand.secondary
                            : brand.primary
                        }
                        track="#dfeaf3"
                        className="mt-4"
                      />
                      <p
                        className="mt-4 w-full rounded-2xl px-4 py-3 text-sm font-medium leading-7 break-words"
                        style={{
                          color: brand.muted,
                          background: "rgba(255,255,255,0.82)",
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Panel>
        ) : null}

        {displayedReport === "landscaping-impact" ? (
        <Panel id="landscaping-impact" theme="landscape">
          <SectionHeader
            eyebrow="Residential Action"
            title="Landscape change is the clearest residential water story"
            subtitle="This section turns the worksheet into a more understandable report: why homeowners switch, how much water the shift can save, and what those savings look like at property scale."
            accent={sectionThemes.landscape.accent}
          />

          <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {utahWaterData.landscapeShift.savingsMetrics.slice(0, 4).map((item) => (
              <SnapshotCard
                key={item.metric}
                label={item.metric}
                value={item.value}
                accent={sectionThemes.landscape.accent}
                glow="rgba(13, 138, 106, 0.18)"
                compact
              />
            ))}
          </div>

          <div className="mt-6">
            <div
              className="overflow-hidden rounded-3xl border bg-white"
              style={chartCardStyle("landscape")}
            >
              <div className="border-b px-6 py-5" style={{ borderColor: brand.border }}>
                <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                  When people switch from lawn to water-wise usage
                </h3>
                <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                  {utahWaterData.landscapeShift.intro}
                </p>
              </div>
              <div className="grid gap-4 p-6">
                {utahWaterData.landscapeShift.switchFactors.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border p-4"
                    style={{
                      borderColor: sectionThemes.landscape.border,
                      background:
                        "linear-gradient(180deg, rgba(13, 138, 106, 0.16) 0%, rgba(255,255,255,1) 100%)",
                    }}
                  >
                    <h4 className="font-bold" style={{ color: brand.ink }}>
                      {item.title}
                    </h4>
                    <p className="mt-2 text-sm leading-7" style={{ color: brand.muted }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

            <div
              className="mt-6 overflow-hidden rounded-3xl border bg-white"
              style={chartCardStyle("landscape")}
            >
            <div className="border-b px-6 py-5" style={{ borderColor: brand.border }}>
              <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                How much water is saved by landscape type
              </h3>
              <p className="mt-2 text-sm leading-7" style={{ color: brand.muted }}>
                Readers can compare the baseline lawn with lower-water alternatives
                without decoding the workbook row by row.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead style={tableHeadStyle}>
                  <tr className="text-sm uppercase tracking-wider" style={{ color: brand.muted }}>
                    <th className="px-6 py-4">Landscape Type</th>
                    <th className="px-6 py-4">Annual Water Need</th>
                    <th className="px-6 py-4">Estimated Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {utahWaterData.landscapeShift.savingsTable.map((row) => (
                    <tr key={row.landscapeType} className="border-t" style={{ borderColor: brand.border }}>
                      <td className="px-6 py-4 font-semibold" style={{ color: brand.ink }}>
                        {row.landscapeType}
                      </td>
                      <td className="px-6 py-4" style={{ color: brand.muted }}>
                        {row.annualWaterNeed}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold" style={{ color: brand.primary }}>
                          {row.percentageSaved}
                        </div>
                        <MiniMeter
                          value={landscapeSavingsLevels[row.percentageSaved] || 0}
                          max={70}
                          color={
                            row.percentageSaved === "0% (baseline)"
                              ? brand.accent
                              : sectionThemes.landscape.accent
                          }
                          track="#dcefeb"
                          className="mt-2"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
        ) : null}

        {displayedReport === "water-rebates" ? (
        <Panel id="water-rebates" theme="rebates">
          <SectionHeader
            eyebrow="Rebates"
            title="Utah rebate programs make the savings story easier to act on"
            subtitle="The table shows scale and the cards below show the kinds of upgrades residents actually see and understand."
            accent={sectionThemes.rebates.accent}
          />

          <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {utahWaterData.rebates.landscapeProgram.map((item) => (
              <SnapshotCard
                key={item.metric}
                label={item.metric}
                value={item.actual2024}
                note={`2025 estimate: ${item.estimate2025}`}
                accent={sectionThemes.rebates.accent}
                glow="rgba(183, 121, 54, 0.12)"
                compact
              />
            ))}
          </div>

          <div className="grid gap-6 xl:grid-cols-[1fr_0.95fr]">
            <div
              className="overflow-hidden rounded-3xl border bg-white"
              style={chartCardStyle("rebates")}
            >
              <div className="border-b px-6 py-5" style={{ borderColor: brand.border }}>
                <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                  {utahWaterData.rebates.landscapeProgramTitle}
                </h3>
                <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                  Rebates help translate conservation into a practical homeowner decision:
                  replace turf, reduce irrigation demand, and lower long-term outdoor use.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead style={tableHeadStyle}>
                    <tr className="text-sm uppercase tracking-wider" style={{ color: brand.muted }}>
                      <th className="px-6 py-4">Metric</th>
                      <th className="px-6 py-4">2024 Actuals</th>
                      <th className="px-6 py-4">2025 Estimates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {utahWaterData.rebates.landscapeProgram.map((row) => (
                      <tr key={row.metric} className="border-t" style={{ borderColor: brand.border }}>
                        <td className="px-6 py-4 font-semibold" style={{ color: brand.ink }}>
                          {row.metric}
                        </td>
                        <td className="px-6 py-4" style={{ color: brand.muted }}>
                          {row.actual2024}
                        </td>
                        <td className="px-6 py-4" style={{ color: brand.muted }}>
                          {row.estimate2025}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-5 text-sm" style={{ color: brand.muted }}>
                Source:{" "}
                <a
                  href={utahWaterData.rebates.source}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: sectionThemes.rebates.accent }}
                  className="font-semibold hover:underline"
                >
                  slowtheflow.org/rebates
                </a>
              </div>
            </div>

            <div className="grid gap-4">
              {utahWaterData.rebates.nonLandscapePrograms.map((item, index) => (
                <div
                  key={item.category}
                  className="rounded-3xl border bg-white p-5 shadow-sm"
                  style={{
                    borderColor: sectionThemes.rebates.border,
                    background:
                      "linear-gradient(180deg, rgba(201, 138, 67, 0.14) 0%, rgba(255,255,255,1) 62%)",
                  }}
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <h3 className="text-lg font-bold" style={{ color: brand.ink }}>
                      {item.category}
                    </h3>
                    <span
                      className="rounded-full px-3 py-1 text-sm font-semibold text-white"
                      style={{ background: sectionThemes.rebates.accent }}
                    >
                      {item.incentive}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                    {item.impact}
                  </p>
                  <MiniMeter
                    value={index + 1}
                    max={utahWaterData.rebates.nonLandscapePrograms.length}
                    color={sectionThemes.rebates.accent}
                    track="#f2e7dd"
                  />
                </div>
              ))}
            </div>
          </div>
        </Panel>
        ) : null}

        {displayedReport === "great-salt-lake" ? (
        <Panel id="great-salt-lake" theme="lake">
          <SectionHeader
            eyebrow="Great Salt Lake"
            title="Why residential savings matter downstream"
            subtitle="This section makes the basin connection clearer: conserved water does not stop at the yard level if it stays in the system and moves through key tributaries."
            accent={sectionThemes.lake.accent}
          />

          <div className="mb-6 grid gap-4 lg:grid-cols-3">
            <SnapshotCard
              label="Largest Tributary"
              value="Bear River"
              note="It carries 60% of tracked inflow into the Great Salt Lake system."
              accent={brand.primary}
              glow="rgba(11, 79, 138, 0.14)"
              compact
            />
            <SnapshotCard
              label="Tracked Buffer"
              value={`${formatNumber(trackedConservationBuffer)} AF`}
              note="The listed conservation buffers show the scale of water that can matter to lake-supporting inflows."
              accent={brand.aqua}
              glow="rgba(116, 199, 198, 0.14)"
              compact
            />
            <SnapshotCard
              label="Key Tributaries"
              value={`${utahWaterData.greatSaltLake.tributaries.length}`}
              note="The table below highlights the major channels connecting local conservation to lake outcomes."
              accent={brand.wetland}
              glow="rgba(63, 138, 99, 0.12)"
              compact
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {utahWaterData.greatSaltLake.strategicImportance.map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border p-5"
                style={{
                borderColor: sectionThemes.lake.border,
                background:
                    "linear-gradient(180deg, rgba(46, 100, 141, 0.12) 0%, rgba(255,255,255,1) 62%)",
              }}
            >
                <h3 className="text-lg font-bold" style={{ color: brand.ink }}>
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-7" style={{ color: brand.muted }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div
            className="mt-6 overflow-hidden rounded-3xl border bg-white"
            style={chartCardStyle("lake")}
          >
            <div className="border-b px-6 py-5" style={{ borderColor: brand.border }}>
              <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                Primary tributaries to the Great Salt Lake
              </h3>
              <p className="mt-2 text-sm leading-7" style={{ color: brand.muted }}>
                The conservation buffer column is especially useful because it helps
                connect local savings to lake-supporting inflows.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead style={tableHeadStyle}>
                  <tr className="text-sm uppercase tracking-wider" style={{ color: brand.muted }}>
                    <th className="px-6 py-4">Tributary</th>
                    <th className="px-6 py-4">Inflow Share</th>
                    <th className="px-6 py-4">Snowpack</th>
                    <th className="px-6 py-4">Avg. Annual Volume</th>
                    <th className="px-6 py-4">Current Flow</th>
                    <th className="px-6 py-4">Conservation Buffer</th>
                    <th className="px-6 py-4">Entry Point</th>
                  </tr>
                </thead>
                <tbody>
                  {utahWaterData.greatSaltLake.tributaries.map((row) => (
                    <tr key={row.tributary} className="border-t" style={{ borderColor: brand.border }}>
                      <td className="px-6 py-4 font-semibold" style={{ color: brand.ink }}>
                        {row.tributary}
                      </td>
                      <td className="px-6 py-4" style={{ color: brand.muted }}>
                        <div>{row.inflowPercent}%</div>
                        <MiniMeter
                          value={row.inflowPercent}
                          max={maxTributaryShare}
                          color={brand.primary}
                          track="#dce9f7"
                          className="mt-2"
                        />
                      </td>
                      <td className="px-6 py-4" style={{ color: brand.muted }}>
                        {row.snowpack}
                      </td>
                      <td className="px-6 py-4" style={{ color: brand.muted }}>
                        {formatNumber(row.annualVolumeAf)} AF
                      </td>
                      <td className="px-6 py-4" style={{ color: brand.muted }}>
                        {row.currentFlow}
                      </td>
                      <td className="px-6 py-4 font-semibold" style={{ color: brand.primary }}>
                        {row.conservationBufferAf ? (
                          <>
                            <div>{`${formatNumber(row.conservationBufferAf)} AF`}</div>
                            <MiniMeter
                              value={row.conservationBufferAf}
                              max={maxTributaryBuffer}
                              color={brand.aqua}
                              track="#d8f2f8"
                              className="mt-2"
                            />
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4" style={{ color: brand.muted }}>
                        {row.entryPoint}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
        ) : null}

        {displayedReport === "county-water" ? (
        <Panel id="county-water" theme="county">
          <SectionHeader
            eyebrow="County Comparison"
            title="Per-capita water usage by county in Utah"
            subtitle="Use the chart filters to compare counties by largest gaps, highest 2024 use, closest-to-goal performance, or counties already at or below target."
            accent={sectionThemes.county.accent}
          />

          <div className="grid gap-4 lg:grid-cols-3">
            <div
              className="rounded-3xl border p-4 sm:p-5"
              style={{
                borderColor: sectionThemes.county.border,
                background:
                  "linear-gradient(180deg, rgba(30, 142, 90, 0.18) 0%, rgba(243, 247, 253, 1) 60%)",
              }}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] sm:text-xs sm:tracking-widest" style={{ color: brand.muted }}>
                On Track
              </p>
              <p className="mt-3 break-words text-2xl font-bold leading-tight sm:text-3xl" style={{ color: brand.primary }}>
                {countiesAtGoal}
              </p>
              <p className="mt-2 break-words text-sm leading-6" style={{ color: brand.muted }}>
                counties are already at or below their 2030 target.
              </p>
            </div>
            <div
              className="rounded-3xl border p-4 sm:p-5"
              style={{
                borderColor: sectionThemes.county.border,
                background:
                  "linear-gradient(180deg, rgba(201, 138, 67, 0.14) 0%, rgba(243, 247, 253, 1) 62%)",
              }}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] sm:text-xs sm:tracking-widest" style={{ color: brand.muted }}>
                Closest Above Goal
              </p>
              <p className="mt-3 break-words text-2xl font-bold leading-tight sm:text-3xl" style={{ color: brand.primary }}>
                {closestToGoalAbove.county}
              </p>
              <p className="mt-2 break-words text-sm leading-6" style={{ color: brand.muted }}>
                {formatGap(closestToGoalAbove.gap)} in 2024, which means it is near
                the target line.
              </p>
            </div>
            <div
              className="rounded-3xl border p-4 sm:p-5"
              style={{
                borderColor: sectionThemes.county.border,
                background:
                  "linear-gradient(180deg, rgba(217, 79, 61, 0.18) 0%, rgba(243, 247, 253, 1) 60%)",
              }}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] sm:text-xs sm:tracking-widest" style={{ color: brand.muted }}>
                Furthest Above Goal
              </p>
              <p className="mt-3 break-words text-2xl font-bold leading-tight sm:text-3xl" style={{ color: brand.accent }}>
                {furthestAboveGoal.county}
              </p>
              <p className="mt-2 break-words text-sm leading-6" style={{ color: brand.muted }}>
                {formatGap(furthestAboveGoal.gap)} in 2024, the widest gap in the report.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <div
              className="rounded-3xl border p-5"
              style={chartCardStyle("county")}
            >
              <div className="flex flex-wrap gap-3">
                {countyViewButtons.map((button) => {
                  const isActive = countyView === button.key;

                  return (
                    <ActionButton1
                      key={button.key}
                      data={button.label}
                      onClick={() => setCountyView(button.key)}
                      showIcon={false}
                      buttonClassName="px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                      buttonStyle={{
                        background: isActive ? brand.primary : brand.foam,
                        color: isActive ? "#ffffff" : brand.primary,
                        borderRadius: "18px",
                        transform: isActive ? "translateY(-2px) scale(1.02)" : "none",
                        boxShadow: isActive
                          ? "0 10px 24px rgba(30, 92, 138, 0.22)"
                          : "0 4px 12px rgba(27, 51, 69, 0.08)",
                        letterSpacing: isActive ? "0.03em" : "0.01em",
                      }}
                      ariaPressed={isActive}
                    />
                  );
                })}
              </div>

              <p className="mt-4 text-sm leading-7" style={{ color: brand.muted }}>
                {countyViewMeta.helper}. Hover over the bars to compare each county&apos;s
                2024 use with its 2030 goal.
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <SnapshotCard
                  label="Highest In View"
                  value={`${countyChartData[0]?.county || "-"} ${countyChartData[0]?.current2024 || ""}`.trim()}
                  note="The active filter always ranks the largest value first."
                  accent={brand.accent}
                  glow="rgba(201, 138, 67, 0.12)"
                  compact
                />
                <SnapshotCard
                  label="View Size"
                  value={`${countyChartData.length} Counties`}
                  note="The chart keeps the comparison set focused so the ranking stays readable."
                  accent={brand.primary}
                  glow="rgba(11, 79, 138, 0.12)"
                  compact
                />
                <SnapshotCard
                  label="Strongest Performer In View"
                  value={
                    [...countyChartData].sort((a, b) => a.gap - b.gap)[0]?.county || "-"
                  }
                  note="Lower gaps are better, and negative values mean the county is already below goal."
                  accent={brand.wetland}
                  glow="rgba(63, 138, 99, 0.12)"
                  compact
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <span
                  className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ background: "rgba(30, 142, 90, 0.16)", color: countyBarPalette.good }}
                >
                  On track
                </span>
                <span
                  className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ background: "rgba(201, 138, 67, 0.14)", color: countyBarPalette.caution }}
                >
                  Close gap
                </span>
                <span
                  className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ background: "rgba(217, 79, 61, 0.16)", color: countyBarPalette.danger }}
                >
                  Largest gap
                </span>
              </div>

              <div className="mt-6 h-[420px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={countyChartData}
                    layout="vertical"
                    margin={{ top: 10, right: 24, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#d8e6fb" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: brand.muted }} />
                    <YAxis
                      type="category"
                      dataKey="county"
                      width={90}
                      tick={{ fontSize: 12, fill: brand.ink }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="current2024"
                      name="2024 use"
                      radius={[0, 8, 8, 0]}
                    >
                      {countyChartData.map((entry) => (
                        <Cell
                          key={`current-${entry.county}`}
                          fill={getCountyCurrentBarColor(entry)}
                        />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="goal2030"
                      name="2030 goal"
                      fill={countyBarPalette.goal}
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div
            className="mt-6 overflow-hidden rounded-3xl border bg-white"
            style={{
              borderColor: sectionThemes.county.border,
              boxShadow: "0 20px 38px rgba(169, 107, 58, 0.12)",
            }}
          >
            <div className="border-b px-6 py-5" style={{ borderColor: brand.border }}>
              <h3 className="text-xl font-bold" style={{ color: brand.ink }}>
                Per-capita water usage by county in Utah
              </h3>
              <p className="mt-2 text-sm leading-7" style={{ color: brand.muted }}>
                This full table is alphabetized for easier lookup. Use the gap-to-goal
                column to see which counties are already meeting the target and which
                ones still have the most distance to cover.
              </p>
              <a
                href={utahWaterData.countyUsage.source}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-block text-sm font-semibold hover:underline"
                style={{ color: brand.primary }}
              >
                County source
              </a>
            </div>
            <div className="max-h-[540px] overflow-auto">
                <table className="min-w-full text-left">
                  <thead style={tableHeadStyle}>
                    <tr className="text-sm uppercase tracking-wider" style={{ color: brand.muted }}>
                      <th className="sticky top-0 z-10 px-5 py-4" style={stickyTableHeadCellStyle}>County</th>
                      <th className="sticky top-0 z-10 px-5 py-4" style={stickyTableHeadCellStyle}>2024</th>
                      <th className="sticky top-0 z-10 px-5 py-4" style={stickyTableHeadCellStyle}>2030 Goal</th>
                      <th className="sticky top-0 z-10 whitespace-nowrap px-5 py-4" style={stickyTableHeadCellStyle}>Gap To Goal</th>
                      <th className="sticky top-0 z-10 px-5 py-4" style={stickyTableHeadCellStyle}>2020-2024 Avg</th>
                      <th className="sticky top-0 z-10 px-5 py-4" style={stickyTableHeadCellStyle}>2015-2019 Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                  {alphabeticalCountyRows.map((row) => (
                      <tr
                        key={row.county}
                        className="border-t odd:bg-white even:bg-slate-50/50"
                        style={{ borderColor: brand.border }}
                      >
                        <td className="px-5 py-4 font-semibold" style={{ color: brand.ink }}>
                          {row.county}
                        </td>
                        <td className="px-5 py-4 font-semibold" style={{ color: brand.ink }}>
                          <div>{row.current2024}</div>
                          <MiniMeter
                            value={row.current2024}
                            max={maxCountyUse}
                            color={getCountyCurrentBarColor(row)}
                            track="#edf3f8"
                            className="mt-2"
                          />
                        </td>
                        <td className="px-5 py-4" style={{ color: brand.muted }}>
                          <div>{row.goal2030}</div>
                          <MiniMeter
                            value={row.goal2030}
                            max={maxCountyUse}
                            color={countyBarPalette.goal}
                            track="#edf3f8"
                            className="mt-2"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className="inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold"
                            style={getGapChipStyle(row.gap)}
                          >
                            {formatGap(row.gap)}
                          </span>
                          <MiniMeter
                            value={Math.abs(row.gap)}
                            max={maxCountyGap}
                            color={getCountyCurrentBarColor(row)}
                            track="#f2f4f7"
                            className="mt-2"
                          />
                        </td>
                        <td className="px-5 py-4" style={{ color: brand.muted }}>
                          {row.avg2020to2024}
                        </td>
                        <td className="px-5 py-4" style={{ color: brand.muted }}>
                          {row.avg2015to2019}
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
        ) : null}
        <div ref={reportContentEndRef} />
        </div>

        <Panel theme="overview">
          <SectionHeader
            eyebrow="Quick Definitions"
            title="Terms used throughout the report"
            subtitle="The site already uses a consistent global font. What readers need here is term clarity, so these definitions reduce friction before they reach the charts and tables."
            accent={sectionThemes.overview.accent}
          />
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {unitGuide.map((item) => (
              <div
                key={item.term}
                className="rounded-2xl border px-4 py-3 sm:px-5 sm:py-4"
                style={{
                  borderColor: brand.border,
                  background: brand.panelSoft,
                }}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <span
                    className="inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white"
                    style={{ background: brand.primary }}
                  >
                    {item.term}
                  </span>
                  <p
                    className="text-sm font-semibold leading-6"
                    style={{ color: brand.ink }}
                  >
                    {item.meaning}
                  </p>
                </div>
                <p
                  className="mt-2 text-sm leading-6"
                  style={{ color: brand.muted }}
                >
                  {item.explanation}
                </p>
              </div>
            ))}
          </div>
        </Panel>

        <Panel theme="overview">
          <SectionHeader
            eyebrow="Sources"
            title="Source links"
            subtitle="These are the report links used to assemble the Utah Water page."
            accent={sectionThemes.overview.accent}
          />
          <div className="grid gap-3">
            {utahWaterData.sources.map((source) => (
              <a
                key={source}
                href={source}
                target="_blank"
                rel="noreferrer"
                className="rounded-2xl border bg-white px-4 py-4 text-sm font-semibold hover:underline"
                style={{ borderColor: brand.border, color: brand.primary }}
              >
                {source}
              </a>
            ))}
          </div>
        </Panel>
      </main>
      <Footer />
      </div>

      <button
        type="button"
        onClick={() => setIsWaterWaysModalOpen(true)}
        className={`fixed bottom-5 right-5 z-40 inline-flex items-center gap-3 rounded-full border px-4 py-3 text-sm font-semibold shadow-lg transition-all duration-200 hover:-translate-y-1 ${
          isWaterWaysModalOpen ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
        style={{
          borderColor: brand.border,
          background:
            "linear-gradient(135deg, rgba(30, 92, 138, 0.96) 0%, rgba(45, 127, 120, 0.92) 100%)",
          color: "#ffffff",
          boxShadow: "0 18px 36px rgba(14, 42, 61, 0.20)",
        }}
      >
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-full"
          style={{ background: "rgba(255,255,255,0.14)" }}
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path
              d="M4 14.5C6.2 12 8.7 10.8 11.6 10.8c3 0 5.4 1.2 7.6 3.7M6.4 17.8c1.4-1.6 3.1-2.4 5.2-2.4 2.1 0 3.8.8 5.2 2.4M9.2 6.8c.9-.9 1.8-1.3 2.8-1.3s1.9.4 2.8 1.3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="hidden sm:inline">Utah Water Ways</span>
      </button>

      <WaterWaysModal
        open={isWaterWaysModalOpen}
        onClose={() => setIsWaterWaysModalOpen(false)}
      />
    </div>
  );
};

export default UtahWater;
